import React, { useState } from 'react';
import { useFeatureFlag } from '@trello/feature-flag-client';
import { MoonshotBox } from './MoonshotBox';
import styles from './MoonshotSuccess.less';
import { Spinner } from 'app/src/components/Spinner';
import {
  Button,
  ButtonSizes,
} from 'app/common/components/controls/button/button';
import { useMoonshotSuccessQuery } from '@trello/graphql/generated';
import { MoonshotSubmitFunction } from './types';

interface MoonshotSuccessProps {
  onSubmit: MoonshotSubmitFunction;
}

export const MoonshotSuccess: React.FC<MoonshotSuccessProps> = ({
  onSubmit,
}) => {
  const [loading, setLoading] = useState(false);
  const { data } = useMoonshotSuccessQuery({
    variables: {
      memberId: 'me',
    },
  });

  const teamName =
    data && data.member && data.member.organizations[0]
      ? data.member.organizations[0].displayName
      : '';

  const nextStep: React.FormEventHandler = async (e) => {
    e.preventDefault();

    if (loading) {
      return;
    }

    setLoading(true);
    await onSubmit();
    setLoading(false);
  };

  const isFreeTrialExperiment = useFeatureFlag('btg.bc-free-trial', false);

  return (
    <>
      <MoonshotBox className={styles.box}>
        <div className={styles.headerWrapper}>
          <img
            src={require('resources/images/moonshot/tacoCompletionImage.svg')}
            alt=""
          />

          {isFreeTrialExperiment ? (
            <>
              <p>{teamName} is ready for action!</p>
              <h1>
                Way to go! Your team is set up and ready to start using Trello.
              </h1>
            </>
          ) : (
            <>
              <h1>Way to go! You’re all set up.</h1>
              <p>{teamName} is ready for action!</p>
            </>
          )}
        </div>
        <p className={styles.content}>
          {isFreeTrialExperiment ? (
            <span className={styles.freeTrial}>
              We'll{' '}
              <strong>
                <i>
                  start by introducing you to your new Business Class features
                </i>
              </strong>{' '}
              so you can make the most out of your 30 days! You can also skip
              the introduction to start using Trello or invite more people.
            </span>
          ) : (
            <>
              <strong>
                <i>We’ve added 4 boards to your team</i>
              </strong>{' '}
              to help you get started. Feel free to dive in, invite more team
              members, and start getting things done!
            </>
          )}
        </p>
        <Button
          className={styles.button}
          size={ButtonSizes.FULLWIDTH}
          onClick={nextStep}
          disabled={loading}
        >
          {loading ? (
            <Spinner centered />
          ) : isFreeTrialExperiment ? (
            'Get Started'
          ) : (
            'Get Started with Trello'
          )}
        </Button>
      </MoonshotBox>
      {!isFreeTrialExperiment ? (
        <div className={styles.bannerWrapper}>
          <a
            data-test-id="googleStoreLink"
            href="https://play.google.com/store/apps/details?id=com.trello"
            target="_blank"
          >
            <img
              className={styles.playStoreBanner}
              alt="Get it on Google Play"
              src={require('resources/images/buttons/btn-playstore-2.png')}
            />
          </a>
          <a
            data-test-id="appleStoreLink"
            href="https://apps.apple.com/us/app/trello-organize-anything/id461504587"
            target="_blank"
          >
            <img
              className={styles.appStoreBanner}
              alt="Download on the App Store"
              src={require('resources/images/buttons/btn-appstore-2.svg')}
            />
          </a>
        </div>
      ) : null}
    </>
  );
};
